import { useEffect, useRef } from 'react';
import { trackModalInteraction } from '../config/clarity';

/**
 * Custom hook for tracking modal interactions in Clarity
 * @param {string} modalName - The name of the modal
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {Object} metadata - Optional metadata for the modal
 * @returns {Object} - Functions for tracking modal interactions
 */
export const useClarityModalTracking = (modalName, isOpen, metadata = {}) => {
  const prevIsOpen = useRef(isOpen);
  
  useEffect(() => {
    // Only track if the open state has changed
    if (prevIsOpen.current !== isOpen) {
      // Track modal open/close
      trackModalInteraction(
        modalName,
        isOpen ? 'open' : 'close',
        metadata
      );
      
      // Update previous state
      prevIsOpen.current = isOpen;
    }
  }, [isOpen, modalName, metadata]);
  
  // Return functions for tracking other modal interactions
  return {
    /**
     * Track a step change within the modal
     * @param {string} stepName - The name of the step
     * @param {Object} stepMetadata - Additional metadata for this step
     */
    trackStep: (stepName, stepMetadata = {}) => {
      trackModalInteraction(modalName, 'step_change', {
        step: stepName,
        ...metadata,
        ...stepMetadata
      });
    },
    
    /**
     * Track a custom action within the modal
     * @param {string} action - The action being performed
     * @param {Object} actionMetadata - Additional metadata for this action
     */
    trackAction: (action, actionMetadata = {}) => {
      trackModalInteraction(modalName, action, {
        ...metadata,
        ...actionMetadata
      });
    }
  };
};

export default useClarityModalTracking;